import { Button, Chip, Stack } from '@mui/material';
import { format } from 'date-fns';
import { compact, isEmpty, values } from 'lodash';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { buildFacilityLabelLookup } from 'common/utils/facilityLabel';
import { selectAuthorizedFacilities } from 'store/selectors/facilitySelectors';
import { filterReversalQueue } from 'store/thunks/reversalQueueThunks';
import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';

const EMPTY_FILTER = {
  fulfillingPharmacyId: undefined,
  createdAtFrom: undefined,
  createdAtTo: undefined,
  lotNumber: undefined,
  drugName: undefined,
  ndc: undefined,
  isUnverified: undefined,
};

const ReversalReviewFilterStatusBar = () => {
  const dispatch = useDispatch();

  const pharmacies = useSelector(selectAuthorizedFacilities);
  const facilityLabelById = useMemo(() => buildFacilityLabelLookup(pharmacies), [pharmacies]);
  const filter = useSelector(({ inventory }) => inventory.reversalQueue.filter);

  const hasFilters = !isEmpty(compact(values(filter)));

  if (!hasFilters) return null;

  const removeFilter = (keys) =>
    dispatch(
      filterReversalQueue({
        filter: { ...filter, ...Object.fromEntries(keys.map((k) => [k, undefined])) },
      })
    );

  const removeAll = () => dispatch(filterReversalQueue({ filter: EMPTY_FILTER }));

  const dateRangeLabel = (() => {
    const from = filter.createdAtFrom ? format(new Date(filter.createdAtFrom), 'MM/dd/yyyy') : null;
    const to = filter.createdAtTo ? format(new Date(filter.createdAtTo), 'MM/dd/yyyy') : null;
    if (from && to) return `Created Date: ${from} – ${to}`;
    if (from) return `Created Date: From ${from}`;
    return `Created Date: To ${to}`;
  })();

  return (
    <Stack direction='row' spacing={0.75} flexWrap='wrap' alignItems='center'>
      {filter.fulfillingPharmacyId && (
        <Chip
          size='small'
          label={`Facility: ${
            facilityLabelById[filter.fulfillingPharmacyId] ?? filter.fulfillingPharmacyId
          }`}
          onDelete={() => removeFilter(['fulfillingPharmacyId'])}
        />
      )}
      {(filter.createdAtFrom || filter.createdAtTo) && (
        <Chip
          size='small'
          label={dateRangeLabel}
          onDelete={() => removeFilter(['createdAtFrom', 'createdAtTo'])}
        />
      )}
      {filter.lotNumber && (
        <Chip
          size='small'
          label={`Lot #: ${filter.lotNumber}`}
          onDelete={() => removeFilter(['lotNumber'])}
        />
      )}
      {filter.drugName && (
        <Chip
          size='small'
          label={`Drug: ${filter.drugName}`}
          onDelete={() => removeFilter(['drugName'])}
        />
      )}
      {filter.ndc && (
        <Chip size='small' label={`NDC: ${filter.ndc}`} onDelete={() => removeFilter(['ndc'])} />
      )}
      {/* POM-6087 - worth a chip of its own: it hides rows rather than narrowing them by a
          value the reader typed, so a reviewer who forgets it is on would conclude the
          queue is empty. */}
      {filter.isUnverified && (
        <Chip
          size='small'
          label={`${SYSTEM_DEFICIT} claims only`}
          onDelete={() => removeFilter(['isUnverified'])}
          // This theme remaps `warning.main` to the perfectRx navy, so color='warning' paints the
          // chip dark navy with near-black text and the label disappears into the fill. Use the
          // same amber pair the table's own Unverified chip carries.
          sx={{
            bgcolor: '#fff3e0',
            color: '#e65100',
            fontWeight: 600,
            '& .MuiChip-deleteIcon': {
              color: '#e65100',
              opacity: 0.7,
              '&:hover': { color: '#e65100', opacity: 1 },
            },
          }}
        />
      )}
      <Button size='small' onClick={removeAll} sx={{ fontSize: 12 }}>
        Remove All
      </Button>
    </Stack>
  );
};

export default ReversalReviewFilterStatusBar;
